import React, { useState, useEffect, useCallback } from 'react';
import { fromIso, toIso } from '../utils/businessDate';
import { getTenantToday } from '../utils/dateUtils';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign, Shield, FileText, TrendingUp, TrendingDown, BarChart3,
  Download, RefreshCw, ChevronDown, ChevronRight, Scale, PieChart, ArrowLeftRight
} from 'lucide-react';
import { cn } from '../lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BookValueEntry {
  positionId: number; securityId: number; securityName: string; naicDesignation: string;
  parValue: number; purchasePrice: number; bookValue: number; marketValue: number;
  unrealizedGL: number; bookYield: number; couponRate: number; amortizationMethod: string;
}

interface PortfolioBookYieldSummary {
  portfolioId: number; totalBookValue: number; totalMarketValue: number;
  totalUnrealizedGL: number; weightedAvgBookYield: number; weightedAvgCouponRate: number;
  newMoneyRate: number; rolloffRate: number; positionCount: number;
}

interface RBCResult {
  portfolioId: number; asOfDate: string;
  c1BondCharge: number; c1EquityCharge: number; c1MortgageCharge: number;
  c1RealEstateCharge: number; c1Total: number; c2Charge: number;
  c3aCharge: number; c3cCharge: number; c4Charge: number; c0Charge: number;
  totalRBC: number; totalAdjustedCapital: number; rbcRatio: number;
  bondChargeDetail: Array<{ naicDesignation: string; ratingEquivalent: string; bookValue: number; factor: number; charge: number }>;
  authorizedControlLevel: number;
}

interface CapitalEfficiencyEntry {
  securityId: number; securityName: string; naicDesignation: string;
  bookYield: number; c1Factor: number; capitalEfficiency: number;
  bookValue: number; c1Charge: number;
}

interface IMRSummary {
  totalIMRBalance: number; annualAmortizationRate: number; ytdAmortization: number;
  activeEntryCount: number;
  entries: Array<{ id: number; tradeId: number; entryDate: string; realizedGL: number;
    remainingDuration: number; annualAmortization: number; remainingBalance: number; status: string }>;
}

interface IMRProjection { year: number; beginningBalance: number; amortization: number; endingBalance: number; }

interface BookYieldAttribution {
  portfolioId: number; periodStart: string; periodEnd: string;
  startingBookYield: number; endingBookYield: number;
  newMoneyEffect: number; rolloffEffect: number; turnoverEffect: number; amortizationEffect: number;
  newMoneyYield: number; rolloffYield: number; newMoneyAmount: number; rolloffAmount: number; portfolioSize: number;
}

interface StatutoryReconciliation {
  portfolioId: number; asOfDate: string;
  statutoryIncome: number; economicReturn: number;
  bookYieldIncome: number; realizedGains: number; imrAmortization: number; avrContribution: number;
  totalReturnIncome: number; unrealizedGL: number; priceReturn: number;
  reconciliationDifference: number; explanation: string;
}

interface ScheduleDSummary {
  totalPositions: number; totalParValue: number; totalBookValue: number;
  totalMarketValue: number; totalAnnualIncome: number; weightedAvgYield: number;
  byNAICDesignation: Record<string, number>;
  entries: Array<{ cusip: string; issuerName: string; naicDesignation: string; parValue: number;
    bookValue: number; marketValue: number; annualIncome: number; bookYield: number; unrealizedGL: number }>;
}

interface Portfolio { id: number; portfolioName: string; }

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const Container = styled.div`min-height: calc(100vh - 64px); background: transparent;`;
const TabsContainer = styled.div`background: linear-gradient(to bottom, rgba(15, 23, 42, 0.5) 0%, transparent 100%); padding: 1.5rem 2rem 0;`;
const TabsWrapper = styled.div`display: flex; gap: 4px; max-width: 1800px; margin: 0 auto; border-bottom: 1px solid rgba(255, 255, 255, 0.05); overflow-x: auto;`;
const ContentContainer = styled.div`max-width: 1800px; margin: 0 auto; padding: 2rem;`;
const GlassCard = styled.div`background: rgba(30, 41, 59, 0.5); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; overflow: hidden;`;

const AcctTab: React.FC<{ icon: React.ReactNode; label: string; active: boolean; onClick: () => void }> = ({ icon, label, active, onClick }) => (
  <button onClick={onClick} className={cn("group flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all duration-200 relative whitespace-nowrap", active ? "text-white" : "text-slate-400 hover:text-slate-200")}>
    <span className={cn("transition-transform duration-200 group-hover:scale-110", active ? "text-emerald-400" : "text-slate-500")}>{icon}</span>
    {label}
    {active && <motion.div layoutId="acct-nav-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]" />}
  </button>
);

const fmtCurrency = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
const fmtBps = (v: number) => `${(v * 10000).toFixed(1)} bps`;
const fmtPctShort = (v: number) => `${(v * 100).toFixed(2)}%`;

// ---------------------------------------------------------------------------
// Tab 1: Book Yield & Amortized Cost
// ---------------------------------------------------------------------------

const BookYieldTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [summary, setSummary] = useState<PortfolioBookYieldSummary | null>(null);
  const [entries, setEntries] = useState<BookValueEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    Promise.all([
      axios.get(`/api/insurance/accounting/book-yield-summary/${portfolioId}`),
      axios.get(`/api/insurance/accounting/book-value/${portfolioId}`)
    ]).then(([sumRes, entriesRes]) => {
      setSummary(sumRes.data);
      setEntries(Array.isArray(entriesRes.data) ? entriesRes.data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [portfolioId]);

  if (loading) return <GlassCard className="p-12 text-center text-slate-500">Loading book yield data...</GlassCard>;
  if (!summary) return <GlassCard className="p-12 text-center text-slate-500">No book value data available</GlassCard>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <GlassCard className="p-5 border-l-4 border-l-emerald-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Weighted Avg Book Yield</div>
          <div className="text-2xl font-bold text-emerald-400">{fmtPctShort(summary.weightedAvgBookYield)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-blue-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total Book Value</div>
          <div className="text-2xl font-bold text-white">{fmtCurrency(summary.totalBookValue)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-violet-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total Market Value</div>
          <div className="text-2xl font-bold text-white">{fmtCurrency(summary.totalMarketValue)}</div>
        </GlassCard>
        <GlassCard className={cn("p-5 border-l-4", summary.totalUnrealizedGL >= 0 ? "border-l-emerald-500" : "border-l-red-500")}>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Unrealized G/L</div>
          <div className={cn("text-2xl font-bold", summary.totalUnrealizedGL >= 0 ? "text-emerald-400" : "text-red-400")}>
            {fmtCurrency(summary.totalUnrealizedGL)}
          </div>
        </GlassCard>
      </div>

      <GlassCard className="p-6">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Position Book Values ({entries.length} positions)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase border-b border-white/5">
                <th className="text-left py-2 px-3">Security</th>
                <th className="text-center py-2 px-3">NAIC</th>
                <th className="text-right py-2 px-3">Par Value</th>
                <th className="text-right py-2 px-3">Book Value</th>
                <th className="text-right py-2 px-3">Market Value</th>
                <th className="text-right py-2 px-3">Unreal G/L</th>
                <th className="text-right py-2 px-3">Book Yield</th>
                <th className="text-right py-2 px-3">Coupon</th>
              </tr>
            </thead>
            <tbody>
              {entries.slice(0, 50).map((e, i) => (
                <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="py-2 px-3 text-white font-semibold">{e.securityName || `Security #${e.securityId}`}</td>
                  <td className="py-2 px-3 text-center">
                    <span className={cn("px-1.5 py-0.5 text-[10px] font-bold rounded",
                      e.naicDesignation === '1' ? "bg-emerald-500/20 text-emerald-400" :
                      e.naicDesignation === '2' ? "bg-blue-500/20 text-blue-400" :
                      e.naicDesignation === '3' ? "bg-amber-500/20 text-amber-400" :
                      "bg-red-500/20 text-red-400"
                    )}>NAIC {e.naicDesignation || '?'}</span>
                  </td>
                  <td className="py-2 px-3 text-right text-slate-400 font-mono">{fmtCurrency(e.parValue)}</td>
                  <td className="py-2 px-3 text-right text-white font-mono">{fmtCurrency(e.bookValue)}</td>
                  <td className="py-2 px-3 text-right text-slate-300 font-mono">{fmtCurrency(e.marketValue)}</td>
                  <td className={cn("py-2 px-3 text-right font-mono font-bold", e.unrealizedGL >= 0 ? "text-emerald-400" : "text-red-400")}>
                    {fmtCurrency(e.unrealizedGL)}
                  </td>
                  <td className="py-2 px-3 text-right text-emerald-400 font-mono">{fmtPctShort(e.bookYield)}</td>
                  <td className="py-2 px-3 text-right text-slate-400 font-mono">{fmtPctShort(e.couponRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 2: RBC Capital
// ---------------------------------------------------------------------------

const RBCCapitalTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [rbc, setRBC] = useState<RBCResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    axios.get(`/api/insurance/accounting/rbc/${portfolioId}`)
      .then(res => setRBC(res.data))
      .catch(() => setRBC(null))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  if (loading) return <GlassCard className="p-12 text-center text-slate-500">Calculating RBC...</GlassCard>;
  if (!rbc) return <GlassCard className="p-12 text-center text-slate-500">No RBC data available</GlassCard>;

  const ratioColor = rbc.rbcRatio >= 4.0 ? 'text-emerald-400' : rbc.rbcRatio >= 3.0 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-6">
      {/* RBC Ratio */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <GlassCard className="p-5 border-l-4 border-l-emerald-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">RBC Ratio</div>
          <div className={cn("text-2xl font-bold", ratioColor)}>{(rbc.rbcRatio * 100).toFixed(0)}%</div>
          <div className="text-[10px] text-slate-500 mt-1">Target: 350-500%+</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-blue-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total RBC Required</div>
          <div className="text-2xl font-bold text-white">{fmtCurrency(rbc.totalRBC)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-violet-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Total Adjusted Capital</div>
          <div className="text-2xl font-bold text-white">{fmtCurrency(rbc.totalAdjustedCapital)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-amber-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Auth Control Level</div>
          <div className="text-2xl font-bold text-white">{fmtCurrency(rbc.authorizedControlLevel)}</div>
        </GlassCard>
      </div>

      {/* Capital Components */}
      <GlassCard className="p-6">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">RBC Component Breakdown</h3>
        <div className="space-y-3">
          {[
            { label: 'C-1 Bond Risk', value: rbc.c1BondCharge, color: 'bg-blue-500' },
            { label: 'C-1 Equity Risk', value: rbc.c1EquityCharge, color: 'bg-violet-500' },
            { label: 'C-1 Mortgage Risk', value: rbc.c1MortgageCharge, color: 'bg-cyan-500' },
            { label: 'C-1 Real Estate Risk', value: rbc.c1RealEstateCharge, color: 'bg-amber-500' },
            { label: 'C-3a Interest Rate Risk', value: rbc.c3aCharge, color: 'bg-red-500' },
            { label: 'C-2 Insurance Risk', value: rbc.c2Charge, color: 'bg-emerald-500' },
            { label: 'C-4 Business Risk', value: rbc.c4Charge, color: 'bg-slate-500' },
          ].filter(c => c.value > 0).map(comp => {
            const pct = rbc.totalRBC > 0 ? (comp.value / rbc.totalRBC) * 100 : 0;
            return (
              <div key={comp.label} className="flex items-center gap-4">
                <span className="text-sm text-slate-400 w-48">{comp.label}</span>
                <div className="flex-1 h-6 bg-slate-900/50 rounded-full overflow-hidden">
                  <div className={cn("h-full rounded-full", comp.color)} style={{ width: `${Math.min(pct, 100)}%`, opacity: 0.6 }} />
                </div>
                <span className="text-sm font-mono text-white w-28 text-right">{fmtCurrency(comp.value)}</span>
                <span className="text-xs text-slate-500 w-16 text-right">{pct.toFixed(1)}%</span>
              </div>
            );
          })}
          <div className="flex items-center gap-4 pt-2 border-t border-white/10">
            <span className="text-sm text-white font-bold w-48">Total (Covariance Formula)</span>
            <div className="flex-1" />
            <span className="text-sm font-mono text-white font-bold w-28 text-right">{fmtCurrency(rbc.totalRBC)}</span>
          </div>
        </div>
      </GlassCard>

      {/* C-1 Bond Detail */}
      <GlassCard className="p-6">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">C-1 Bond Charges by NAIC Designation</h3>
        <div className="space-y-2">
          {(rbc.bondChargeDetail || []).map((d, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50">
              <div className="flex items-center gap-3">
                <span className={cn("px-2 py-0.5 text-xs font-bold rounded",
                  d.naicDesignation.startsWith('1') ? "bg-emerald-500/20 text-emerald-400" :
                  d.naicDesignation.startsWith('2') ? "bg-blue-500/20 text-blue-400" :
                  "bg-amber-500/20 text-amber-400"
                )}>NAIC {d.naicDesignation}</span>
                <span className="text-sm text-slate-400">{d.ratingEquivalent}</span>
              </div>
              <div className="flex items-center gap-8 text-sm">
                <span className="text-slate-400">Book: {fmtCurrency(d.bookValue)}</span>
                <span className="text-slate-500">Factor: {(d.factor * 100).toFixed(2)}%</span>
                <span className="text-white font-bold">{fmtCurrency(d.charge)}</span>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 3: Capital Efficiency
// ---------------------------------------------------------------------------

const CapitalEfficiencyTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [entries, setEntries] = useState<CapitalEfficiencyEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    axios.get(`/api/insurance/accounting/rbc/capital-efficiency/${portfolioId}`)
      .then(res => setEntries(Array.isArray(res.data) ? res.data : (res.data?.entries || [])))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  if (loading) return <GlassCard className="p-12 text-center text-slate-500">Calculating capital efficiency...</GlassCard>;

  return (
    <div className="space-y-6">
      <GlassCard className="p-6">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">
          Capital Efficiency Ranking — Yield per Unit of C-1 Capital
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase border-b border-white/5">
                <th className="text-left py-2 px-3">Security</th>
                <th className="text-center py-2 px-3">NAIC</th>
                <th className="text-right py-2 px-3">Book Yield</th>
                <th className="text-right py-2 px-3">C-1 Factor</th>
                <th className="text-right py-2 px-3">Efficiency</th>
                <th className="text-right py-2 px-3">Book Value</th>
                <th className="text-right py-2 px-3">C-1 Charge</th>
              </tr>
            </thead>
            <tbody>
              {entries.slice(0, 50).map((e, i) => (
                <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                  <td className="py-2 px-3 text-white font-semibold">{e.securityName || `Security #${e.securityId}`}</td>
                  <td className="py-2 px-3 text-center">
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-400">NAIC {e.naicDesignation}</span>
                  </td>
                  <td className="py-2 px-3 text-right text-emerald-400 font-mono">{fmtPctShort(e.bookYield || 0)}</td>
                  <td className="py-2 px-3 text-right text-slate-400 font-mono">{((e.c1Factor || 0) * 100).toFixed(2)}%</td>
                  <td className="py-2 px-3 text-right">
                    <span className={cn("font-bold font-mono", (e.capitalEfficiency || 0) >= 5 ? "text-emerald-400" : (e.capitalEfficiency || 0) >= 2 ? "text-blue-400" : "text-amber-400")}>
                      {(e.capitalEfficiency || 0).toFixed(1)}x
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right text-white font-mono">{fmtCurrency(e.bookValue || 0)}</td>
                  <td className="py-2 px-3 text-right text-red-400 font-mono">{fmtCurrency(e.c1Charge || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 4: IMR/AVR
// ---------------------------------------------------------------------------

const IMRAVRTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [imr, setIMR] = useState<IMRSummary | null>(null);
  const [projection, setProjection] = useState<IMRProjection[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    Promise.all([
      axios.get(`/api/insurance/accounting/imr/${portfolioId}`),
      axios.get(`/api/insurance/accounting/imr/${portfolioId}/projection`, { params: { years: 10 } })
    ]).then(([imrRes, projRes]) => {
      setIMR(imrRes.data);
      setProjection(Array.isArray(projRes.data) ? projRes.data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [portfolioId]);

  if (loading) return <GlassCard className="p-12 text-center text-slate-500">Loading IMR/AVR data...</GlassCard>;

  return (
    <div className="space-y-6">
      {/* IMR Summary */}
      <div className="grid grid-cols-4 gap-4">
        <GlassCard className="p-5 border-l-4 border-l-blue-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">IMR Balance</div>
          <div className="text-2xl font-bold text-white">{fmtCurrency(imr?.totalIMRBalance || 0)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-emerald-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Annual Amortization</div>
          <div className="text-2xl font-bold text-emerald-400">{fmtCurrency(imr?.annualAmortizationRate || 0)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-amber-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">YTD Amortized</div>
          <div className="text-2xl font-bold text-white">{fmtCurrency(imr?.ytdAmortization || 0)}</div>
        </GlassCard>
        <GlassCard className="p-5 border-l-4 border-l-violet-500">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Active Entries</div>
          <div className="text-2xl font-bold text-white">{imr?.activeEntryCount || 0}</div>
        </GlassCard>
      </div>

      {/* IMR Amortization Projection */}
      <GlassCard className="p-6">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">IMR Amortization Projection</h3>
        <div className="space-y-2">
          {projection.map(p => (
            <div key={p.year} className="flex items-center gap-4 px-3 py-2 rounded-lg hover:bg-slate-900/50 text-sm">
              <span className="text-slate-500 font-mono w-12">{p.year}</span>
              <span className="text-slate-400 w-32 text-right">{fmtCurrency(p.beginningBalance)}</span>
              <span className="text-emerald-400 w-32 text-right">+{fmtCurrency(p.amortization)}</span>
              <span className="text-white font-bold w-32 text-right">{fmtCurrency(p.endingBalance)}</span>
              <div className="flex-1 h-3 bg-slate-900/50 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-blue-500/40"
                  style={{ width: `${projection[0]?.beginningBalance ? (p.endingBalance / projection[0].beginningBalance) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
          <div className="text-[10px] text-slate-600 px-3 flex gap-8">
            <span>Year</span><span>Beginning</span><span className="text-emerald-400/60">Released to Income</span><span>Ending</span>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 5: Book Yield Attribution
// ---------------------------------------------------------------------------

const BookYieldAttributionTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [attribution, setAttribution] = useState<BookYieldAttribution | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    const end = getTenantToday();
    const startDay = fromIso(end);
    startDay.setDate(startDay.getDate() - 90);
    const start = toIso(startDay);
    axios.get(`/api/insurance/analytics/book-yield-attribution/${portfolioId}`, { params: { periodStart: start, periodEnd: end } })
      .then(res => setAttribution(res.data))
      .catch(() => setAttribution(null))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  if (loading) return <GlassCard className="p-12 text-center text-slate-500">Calculating attribution...</GlassCard>;
  if (!attribution) return <GlassCard className="p-12 text-center text-slate-500">No attribution data available</GlassCard>;

  const effects = [
    { label: 'Starting Book Yield', value: attribution.startingBookYield, isBase: true },
    { label: 'New Money Effect', value: attribution.newMoneyEffect, detail: `Avg yield: ${fmtPctShort(attribution.newMoneyYield)}, Amount: ${fmtCurrency(attribution.newMoneyAmount)}` },
    { label: 'Rolloff Effect', value: attribution.rolloffEffect, detail: `Avg yield: ${fmtPctShort(attribution.rolloffYield)}, Amount: ${fmtCurrency(attribution.rolloffAmount)}` },
    { label: 'Amortization Effect', value: attribution.amortizationEffect, detail: 'Premium/discount amortization impact' },
    { label: 'Ending Book Yield', value: attribution.endingBookYield, isBase: true },
  ];

  return (
    <div className="space-y-6">
      <GlassCard className="p-6">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Book Yield Bridge — Last 90 Days</h3>
        <div className="space-y-3">
          {effects.map((e, i) => (
            <div key={i} className={cn("flex items-center gap-4 px-4 py-3 rounded-lg", e.isBase ? "bg-slate-800/50" : "bg-slate-900/30")}>
              <span className={cn("text-sm w-48", e.isBase ? "text-white font-bold" : "text-slate-400")}>{e.label}</span>
              {!e.isBase && (
                <span className={cn("text-sm font-bold font-mono w-24 text-right", e.value >= 0 ? "text-emerald-400" : "text-red-400")}>
                  {e.value >= 0 ? '+' : ''}{fmtBps(e.value)}
                </span>
              )}
              {e.isBase && (
                <span className="text-sm font-bold font-mono text-white w-24 text-right">{fmtPctShort(e.value)}</span>
              )}
              {'detail' in e && e.detail && <span className="text-xs text-slate-600 ml-4">{e.detail}</span>}
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 6: Statutory Reconciliation
// ---------------------------------------------------------------------------

const StatutoryReconTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [recon, setRecon] = useState<StatutoryReconciliation | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    const end = getTenantToday();
    const startDay = fromIso(end);
    startDay.setDate(startDay.getDate() - 90);
    const start = toIso(startDay);
    axios.get(`/api/insurance/analytics/statutory-reconciliation/${portfolioId}`, { params: { periodStart: start, periodEnd: end } })
      .then(res => setRecon(res.data))
      .catch(() => setRecon(null))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  if (loading) return <GlassCard className="p-12 text-center text-slate-500">Loading reconciliation...</GlassCard>;
  if (!recon) return <GlassCard className="p-12 text-center text-slate-500">No reconciliation data available</GlassCard>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Statutory Side */}
        <GlassCard className="p-6">
          <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-widest mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Statutory Income (SAP)
          </h3>
          <div className="space-y-2">
            {[
              { label: 'Book Yield Income', value: recon.bookYieldIncome },
              { label: 'Realized Gains/Losses', value: recon.realizedGains },
              { label: 'IMR Amortization', value: recon.imrAmortization },
              { label: 'AVR Contribution', value: recon.avrContribution },
            ].map(item => (
              <div key={item.label} className="flex justify-between py-1">
                <span className="text-sm text-slate-400">{item.label}</span>
                <span className={cn("text-sm font-mono", item.value >= 0 ? "text-white" : "text-red-400")}>{fmtCurrency(item.value)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 border-t border-white/10">
              <span className="text-sm text-white font-bold">Total Statutory Income</span>
              <span className="text-sm font-mono text-emerald-400 font-bold">{fmtCurrency(recon.statutoryIncome)}</span>
            </div>
          </div>
        </GlassCard>

        {/* Economic Side */}
        <GlassCard className="p-6">
          <h3 className="text-sm font-bold text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Economic Return (Total Return)
          </h3>
          <div className="space-y-2">
            {[
              { label: 'Coupon/Interest Income', value: recon.totalReturnIncome },
              { label: 'Unrealized G/L Change', value: recon.unrealizedGL },
              { label: 'Price Return', value: recon.priceReturn },
            ].map(item => (
              <div key={item.label} className="flex justify-between py-1">
                <span className="text-sm text-slate-400">{item.label}</span>
                <span className={cn("text-sm font-mono", item.value >= 0 ? "text-white" : "text-red-400")}>{fmtCurrency(item.value)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 border-t border-white/10">
              <span className="text-sm text-white font-bold">Total Economic Return</span>
              <span className="text-sm font-mono text-blue-400 font-bold">{fmtCurrency(recon.economicReturn)}</span>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Reconciliation */}
      <GlassCard className="p-6">
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
          <ArrowLeftRight className="w-4 h-4" /> Reconciliation
        </h3>
        <div className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50">
          <div className="text-center">
            <div className="text-xs text-slate-500 mb-1">Statutory</div>
            <div className="text-lg font-bold text-emerald-400">{fmtCurrency(recon.statutoryIncome)}</div>
          </div>
          <div className="text-2xl text-slate-600">vs</div>
          <div className="text-center">
            <div className="text-xs text-slate-500 mb-1">Economic</div>
            <div className="text-lg font-bold text-blue-400">{fmtCurrency(recon.economicReturn)}</div>
          </div>
          <div className="text-2xl text-slate-600">=</div>
          <div className="text-center">
            <div className="text-xs text-slate-500 mb-1">Difference</div>
            <div className={cn("text-lg font-bold", recon.reconciliationDifference >= 0 ? "text-amber-400" : "text-red-400")}>
              {fmtCurrency(recon.reconciliationDifference)}
            </div>
          </div>
        </div>
        {recon.explanation && (
          <div className="mt-3 text-sm text-slate-400 italic px-1">{recon.explanation}</div>
        )}
      </GlassCard>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab 7: NAIC Schedule Generator
// ---------------------------------------------------------------------------

const ScheduleGeneratorTab: React.FC<{ portfolioId: number | null }> = ({ portfolioId }) => {
  const [scheduleD, setScheduleD] = useState<ScheduleDSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeSchedule, setActiveSchedule] = useState<'D' | 'BA' | 'B'>('D');

  const generate = async (type: 'D' | 'BA' | 'B') => {
    if (!portfolioId) return;
    setLoading(true);
    setActiveSchedule(type);
    try {
      const endpoint = type === 'D' ? 'schedule-d' : type === 'BA' ? 'schedule-ba' : 'schedule-b';
      const res = await axios.get(`/api/insurance/accounting/${endpoint}/${portfolioId}`);
      setScheduleD(res.data);
    } catch { setScheduleD(null); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <GlassCard className="p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-400" /> NAIC Schedule Generator
        </h3>
        <div className="flex gap-3">
          {[
            { key: 'D' as const, label: 'Schedule D', desc: 'Bonds — CUSIP, par, book, market, NAIC' },
            { key: 'BA' as const, label: 'Schedule BA', desc: 'Other Invested Assets — alternatives, private credit' },
            { key: 'B' as const, label: 'Schedule B', desc: 'Mortgage Loans — LTV, DSCR, property type' },
          ].map(s => (
            <button key={s.key} onClick={() => generate(s.key)}
              className={cn("flex-1 p-4 rounded-xl border transition-all text-left",
                activeSchedule === s.key && scheduleD ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-900/50 border-white/5 hover:border-white/10")}>
              <div className="text-sm font-bold text-white mb-0.5">{s.label}</div>
              <div className="text-xs text-slate-500">{s.desc}</div>
            </button>
          ))}
        </div>
      </GlassCard>

      {loading && <GlassCard className="p-12 text-center text-slate-500">Generating schedule...</GlassCard>}

      {scheduleD && !loading && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <GlassCard className="p-5"><div className="text-xs text-slate-500 mb-1">Positions</div><div className="text-xl font-bold text-white">{scheduleD.totalPositions}</div></GlassCard>
            <GlassCard className="p-5"><div className="text-xs text-slate-500 mb-1">Total Par</div><div className="text-xl font-bold text-white">{fmtCurrency(scheduleD.totalParValue)}</div></GlassCard>
            <GlassCard className="p-5"><div className="text-xs text-slate-500 mb-1">Total Book</div><div className="text-xl font-bold text-white">{fmtCurrency(scheduleD.totalBookValue)}</div></GlassCard>
            <GlassCard className="p-5"><div className="text-xs text-slate-500 mb-1">Avg Yield</div><div className="text-xl font-bold text-emerald-400">{fmtPctShort(scheduleD.weightedAvgYield)}</div></GlassCard>
          </div>

          {/* By NAIC Designation */}
          {scheduleD.byNAICDesignation && Object.keys(scheduleD.byNAICDesignation).length > 0 && (
            <GlassCard className="p-6">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-3">Distribution by NAIC Designation</h3>
              <div className="flex gap-3">
                {Object.entries(scheduleD.byNAICDesignation).sort(([a], [b]) => a.localeCompare(b)).map(([naic, amount]) => (
                  <div key={naic} className="flex-1 p-3 rounded-lg bg-slate-900/50 text-center">
                    <div className="text-xs text-slate-500 mb-1">NAIC {naic}</div>
                    <div className="text-sm font-bold text-white">{fmtCurrency(amount)}</div>
                    <div className="text-[10px] text-slate-600">{scheduleD.totalBookValue > 0 ? ((amount / scheduleD.totalBookValue) * 100).toFixed(1) : 0}%</div>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Entry List */}
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">Schedule {activeSchedule} Entries</h3>
              <button className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-lg flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-800/90">
                  <tr className="text-xs text-slate-500 uppercase border-b border-white/5">
                    <th className="text-left py-2 px-3">CUSIP</th>
                    <th className="text-left py-2 px-3">Issuer</th>
                    <th className="text-center py-2 px-3">NAIC</th>
                    <th className="text-right py-2 px-3">Par</th>
                    <th className="text-right py-2 px-3">Book</th>
                    <th className="text-right py-2 px-3">Market</th>
                    <th className="text-right py-2 px-3">Income</th>
                    <th className="text-right py-2 px-3">Yield</th>
                  </tr>
                </thead>
                <tbody>
                  {(scheduleD.entries || []).slice(0, 100).map((e, i) => (
                    <tr key={i} className="border-b border-white/[0.03]">
                      <td className="py-1.5 px-3 text-slate-400 font-mono text-xs">{e.cusip || '—'}</td>
                      <td className="py-1.5 px-3 text-white">{e.issuerName}</td>
                      <td className="py-1.5 px-3 text-center text-xs">{e.naicDesignation}</td>
                      <td className="py-1.5 px-3 text-right text-slate-400 font-mono">{fmtCurrency(e.parValue)}</td>
                      <td className="py-1.5 px-3 text-right text-white font-mono">{fmtCurrency(e.bookValue)}</td>
                      <td className="py-1.5 px-3 text-right text-slate-300 font-mono">{fmtCurrency(e.marketValue)}</td>
                      <td className="py-1.5 px-3 text-right text-emerald-400 font-mono">{fmtCurrency(e.annualIncome)}</td>
                      <td className="py-1.5 px-3 text-right text-slate-400 font-mono">{fmtPctShort(e.bookYield)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </motion.div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

type TabType = 'bookyield' | 'rbc' | 'efficiency' | 'imravr' | 'attribution' | 'recon' | 'schedules';

const InsuranceAccountingScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('bookyield');
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | null>(null);

  useEffect(() => {
    axios.get('/api/dimensions/portfolios').then(res => {
      const list = res.data.content ?? res.data;
      const arr = Array.isArray(list) ? list.slice(0, 50) : [];
      setPortfolios(arr);
      if (arr.length > 0) setSelectedPortfolio(arr[0].id);
    }).catch(() => {});
  }, []);

  return (
    <Container>
      <TabsContainer>
        <div className="max-w-[1800px] mx-auto mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-3">
                <DollarSign className="w-7 h-7 text-emerald-400" />
                Insurance Accounting & Capital
              </h1>
              <p className="text-sm text-slate-500 mt-1">Book yield, RBC capital, IMR/AVR, NAIC schedules, capital efficiency</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-bold text-slate-500 uppercase">Portfolio</label>
              <select value={selectedPortfolio ?? ''} onChange={e => setSelectedPortfolio(Number(e.target.value))}
                className="px-4 py-2 bg-slate-900 border border-white/10 rounded-lg text-white text-sm min-w-[200px]">
                {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
              </select>
            </div>
          </div>
        </div>
        <TabsWrapper>
          <AcctTab icon={<DollarSign className="w-4 h-4" />} label="Book Yield" active={activeTab === 'bookyield'} onClick={() => setActiveTab('bookyield')} />
          <AcctTab icon={<Shield className="w-4 h-4" />} label="RBC Capital" active={activeTab === 'rbc'} onClick={() => setActiveTab('rbc')} />
          <AcctTab icon={<Scale className="w-4 h-4" />} label="Capital Efficiency" active={activeTab === 'efficiency'} onClick={() => setActiveTab('efficiency')} />
          <AcctTab icon={<BarChart3 className="w-4 h-4" />} label="IMR / AVR" active={activeTab === 'imravr'} onClick={() => setActiveTab('imravr')} />
          <AcctTab icon={<TrendingUp className="w-4 h-4" />} label="Yield Attribution" active={activeTab === 'attribution'} onClick={() => setActiveTab('attribution')} />
          <AcctTab icon={<ArrowLeftRight className="w-4 h-4" />} label="Statutory Recon" active={activeTab === 'recon'} onClick={() => setActiveTab('recon')} />
          <AcctTab icon={<FileText className="w-4 h-4" />} label="NAIC Schedules" active={activeTab === 'schedules'} onClick={() => setActiveTab('schedules')} />
        </TabsWrapper>
      </TabsContainer>

      <ContentContainer>
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
            {activeTab === 'bookyield' && <BookYieldTab portfolioId={selectedPortfolio} />}
            {activeTab === 'rbc' && <RBCCapitalTab portfolioId={selectedPortfolio} />}
            {activeTab === 'efficiency' && <CapitalEfficiencyTab portfolioId={selectedPortfolio} />}
            {activeTab === 'imravr' && <IMRAVRTab portfolioId={selectedPortfolio} />}
            {activeTab === 'attribution' && <BookYieldAttributionTab portfolioId={selectedPortfolio} />}
            {activeTab === 'recon' && <StatutoryReconTab portfolioId={selectedPortfolio} />}
            {activeTab === 'schedules' && <ScheduleGeneratorTab portfolioId={selectedPortfolio} />}
          </motion.div>
        </AnimatePresence>
      </ContentContainer>
    </Container>
  );
};

export default InsuranceAccountingScreen;
